import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { api, FALLBACK_TIERS, TIER_RANK } from "../lib/api";

const STYLE = {
  fan: { ring: "border-[#0E1B3D]/15 hover:border-[#D7263D]/60", tag: "Lobby key", bg: "bg-[#FFFFFF]" },
  vip: { ring: "border-[#D7263D] shadow-[0_0_60px_-10px_rgba(215,38,61,0.25)]", tag: "Signature", bg: "bg-[#FFFFFF]" },
  inner: { ring: "border-[#2F6FD6] hover:border-[#1F55B3]", tag: "The vault", bg: "bg-gradient-to-b from-[#E3F1FC] to-[#FFFFFF]" },
};
const TID = { fan: "fan", vip: "vip", inner: "inner-circle" };

function TierCard({ tier, i, currentTier, loading, onSelect, allowSwitch }) {
  const s = STYLE[tier.id];
  const owned = currentTier && (allowSwitch ? currentTier === tier.id : TIER_RANK[currentTier] >= TIER_RANK[tier.id]);
  const verb = TIER_RANK[tier.id] > TIER_RANK[currentTier] ? "Upgrade to" : "Switch to";
  const isCurrent = currentTier === tier.id;
  return (
    <motion.div
      data-testid={`tier-card-${TID[tier.id]}`}
      initial={{ opacity: 0.3, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.9, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6 }}
      className={`relative flex flex-col border ${s.ring} ${s.bg} p-8 sm:p-10 transition-colors duration-500 ${tier.id === "vip" ? "lg:-mt-6 lg:mb-6" : ""}`}
    >
      <div className="flex items-center justify-between">
        <span className="font-mono-x text-[0.68rem] tracking-[0.25em] uppercase text-[#D7263D]">0{i + 1} / {s.tag}</span>
        {isCurrent && <span data-testid={`tier-current-${TID[tier.id]}`} className="font-mono-x text-[0.65rem] tracking-[0.2em] uppercase bg-[#D7263D] text-[#FFF8EE] px-2 py-1">Your tier</span>}
      </div>
      <h3 className="mt-10 font-display text-4xl text-[#0E1B3D]">{tier.name}</h3>
      <p className="mt-6 flex items-baseline gap-2">
        <span className="font-display text-6xl text-[#0E1B3D]">${tier.price}</span>
        <span className="text-sm text-[#4A5878]">/ month</span>
      </p>
      <ul className="mt-10 space-y-4 flex-1">
        {tier.perks.map((p) => (
          <li key={p} className="flex gap-3 text-sm text-[#26355A]"><Check className="w-4 h-4 mt-0.5 text-[#D7263D] shrink-0" />{p}</li>
        ))}
      </ul>
      <button
        data-testid={`tier-subscribe-button-${TID[tier.id]}`}
        disabled={owned || !!loading}
        onClick={() => onSelect(tier.id)}
        className={`mt-12 w-full py-4 text-sm font-semibold tracking-[0.14em] uppercase flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${tier.id === "vip" ? "gold-btn" : "ghost-btn"}`}
      >
        {loading === tier.id && <Loader2 className="w-4 h-4 animate-spin" />}
        {owned ? (isCurrent ? "Current tier" : "Included") : currentTier ? `${verb} ${tier.name}` : `Join ${tier.name}`}
      </button>
    </motion.div>
  );
}

export function TierCards({ currentTier, loading, onSelect, allowSwitch = false }) {
  const [tiers, setTiers] = useState(FALLBACK_TIERS);
  useEffect(() => {
    api.get("/tiers").then((r) => Array.isArray(r.data) && r.data.length && setTiers(r.data)).catch(() => {});
  }, []);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-6 items-stretch">
      {tiers.map((t, i) => <TierCard key={t.id} tier={t} i={i} currentTier={currentTier} loading={loading} onSelect={onSelect} allowSwitch={allowSwitch} />)}
    </div>
  );
}
